import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { dateFormatter } from "utils/formatUtils";
import authService from "services/authService";
import ProjectFormDrawer from "./ProjectFormDrawer";

const ProjectsTableSection = ({ onCreateProject }) => {
  const project = useSelector((state) => state.rfqProjects);
  const entityId = useSelector((state) => state.auth?.user?.userEntity?.entityId);
  const [openProjectDrawer, setOpenProjectDrawer] = useState(false);
  const [projectOverView, setProjectOverView] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [memberLookup, setMemberLookup] = useState(null);
  const [memberAttempt, setMemberAttempt] = useState(0);

  const projects = useMemo(
    () =>
      [...(project.projects || [])].sort(
        (projectA, projectB) =>
          new Date(projectB?.createdAt || 0).getTime() -
          new Date(projectA?.createdAt || 0).getTime()
      ),
    [project.projects]
  );
  const loading = project.loading;
  const directorIdsKey = useMemo(
    () => [...new Set(projects.flatMap((row) => row.directorUserIds || []))].sort((first, second) => first - second).join(","),
    [projects]
  );
  const currentMembers = memberLookup?.entityId === entityId ? memberLookup : null;

  useEffect(() => {
    if (!entityId || !directorIdsKey) return undefined;
    const controller = new AbortController();
    setMemberLookup({ entityId, loading: true, names: new Map(), error: "" });
    authService.getEntityMembers(entityId, { signal: controller.signal }).then(({ members }) => {
      if (!controller.signal.aborted) setMemberLookup({ entityId, loading: false, names: new Map(members.map((member) => [member.userId, member.name])), error: "" });
    }).catch((error) => {
      if (!controller.signal.aborted) setMemberLookup({ entityId, loading: false, names: new Map(), error: error.response?.data?.message || "Unable to load project directors." });
    });
    return () => controller.abort();
  }, [entityId, directorIdsKey, memberAttempt]);

  const filteredProjects = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return projects.filter((projectItem) => {
      const matchesSearch = !normalizedSearchTerm ||
        (projectItem?.name || "").toLowerCase().includes(normalizedSearchTerm);
      const matchesStatus = statusFilter === "all" ||
        (statusFilter === "active"
          ? projectItem?.isActive !== false
          : projectItem?.isActive === false);

      return matchesSearch && matchesStatus;
    });
  }, [projects, searchTerm, statusFilter]);

  const rows = ["Sr No.", "Project Name", "Directors", "Status", "Sub-projects", "Area", "Location", "Create Date"];

  const handleOpenProjectDrawer = (row) => {
    setProjectOverView(row);
    setOpenProjectDrawer(true);
  };

  const handleCloseProjectDrawer = () => {
    setOpenProjectDrawer(false);
  };

  return (
    <div className="m-5 mt-2 flex min-h-0 flex-grow flex-col overflow-hidden rounded-2xl border border-[#E7E7DD] bg-white shadow-sm">
      <section className="border-b border-[#ECECE3] bg-[#FCFCFA] p-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            {/* <p className="text-sm font-semibold text-[#303026]">Projects</p> */}
            <p className="text-xs text-[#7A7A68]">
              {`Showing ${filteredProjects.length} of ${projects.length} projects`}
            </p>
          </div>

          <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
            <fieldset className="flex h-[32px] shrink-0 items-center rounded-sm border border-[#DCDCD0] bg-white p-0.5">
              <legend className="sr-only">Filter projects by status</legend>
              {[
                { value: "all", label: "All" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`relative flex h-full cursor-pointer items-center rounded px-3 text-xs font-semibold transition ${
                    statusFilter === option.value
                      ? "bg-[#FFF0E4] text-[#C2410C] shadow-sm"
                      : "text-[#626252] hover:bg-[#F7F7F2]"
                  }`}
                >
                  <input
                    type="radio"
                    name="project-status-filter"
                    value={option.value}
                    checked={statusFilter === option.value}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    className="sr-only"
                  />
                  {option.label}
                </label>
              ))}
            </fieldset>
            <label htmlFor="project-search" className="sr-only">
              Search projects
            </label>
            <div className="relative w-full md:w-[360px]">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg
                  aria-hidden="true"
                  className="h-4 w-4 text-[#8A8A78]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <input
                type="text"
                id="project-search"
                className="block h-[32px] rounded-sm w-full border border-[#DCDCD0] bg-white py-2 pl-9 pr-3 text-sm text-[#303026] placeholder:text-xs placeholder:text-[#8A8A78] focus:border-[#004491] focus:ring-0"
                placeholder="Search by project name"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            {typeof onCreateProject === "function" && (
              <button
                className="flex cursor-pointer items-center justify-center whitespace-nowrap rounded-sm bg-black px-4 py-2 text-xs text-white transition hover:bg-[#303026]"
                onClick={onCreateProject}
                type="button"
              >
                Create New Project
              </button>
            )}
          </div>
        </div>
      </section>

      {directorIdsKey && currentMembers?.error && (
        <div role="alert" className="flex flex-wrap items-center gap-2 border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          <span>{currentMembers.error}</span>
          <button type="button" onClick={() => setMemberAttempt((value) => value + 1)} className="font-semibold underline">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <div className="loader h-10 w-10 rounded-full border-8 border-t-8 border-gray-200 ease-linear"></div>
        </div>
      ) : filteredProjects.length >= 1 ? (
        <div className="project-table-scroll min-h-0 flex-1 overflow-x-auto overflow-y-auto">
          <table className="w-full min-w-[1100px] table-fixed text-left text-sm text-gray-100 dark:text-gray-400">
            <colgroup>
              <col className="w-[6%]" />
              <col className="w-[22%]" />
              <col className="w-[17%]" />
              <col className="w-[8%]" />
              <col className="w-[9%]" />
              <col className="w-[10%]" />
              <col className="w-[18%]" />
              <col className="w-[10%]" />
            </colgroup>
            <thead className="sticky top-0 z-10 max-w-full bg-[#F2F2EA] text-xs text-[#626252] dark:bg-gray-700 dark:text-gray-400">
              <tr className="border-solid border-[#E0E0D6]">
                {rows.map((title) => (
                  <th
                    key={title}
                    scope="col"
                    className="max-w-52 px-4 py-3 text-[#626252]"
                  >
                    <p className="truncate">{title}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((row, rowIndex) => (
                <tr
                  key={row?._id || rowIndex}
                  className="border-b border-[#ECECE3] text-xs text-black hover:bg-[#FCFCFA]"
                >
                  <td>
                    <div className="flex justify-start px-4 py-3">
                      <p className="text-gray-500">{rowIndex + 1}</p>
                    </div>
                  </td>
                  <td>
                    <div className="flex max-w-60 justify-start px-4 py-3">
                      <button
                        onClick={() => handleOpenProjectDrawer(row)}
                        className="max-w-60"
                        type="button"
                      >
                        <p
                          title={row?.name}
                          className="truncate font-semibold text-blue-700"
                        >
                          {row?.name}
                        </p>
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className="space-y-1 break-words px-4 py-3 text-gray-700">
                      {row.directorUserIds?.length ? (
                        !entityId || currentMembers?.error ? <p className="text-gray-500">Unavailable</p> : !currentMembers || currentMembers.loading ? <p className="text-gray-500">Loading...</p> : row.directorUserIds.map((id) => (
                          <p key={id}>{currentMembers.names.has(id) ? currentMembers.names.get(id) || `Member ${id}` : `Former member ${id}`}</p>
                        ))
                      ) : <p>{row.projectDirector || "Not assigned"}</p>}
                    </div>
                  </td>
                  <td>
                    <div className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-1 text-[11px] font-semibold ${row.isActive === false ? "bg-gray-100 text-gray-600" : "bg-green-50 text-green-700"}`}>
                        {row.isActive === false ? "Inactive" : "Active"}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="px-4 py-3 text-gray-700">{row.subProjectCount || 0}</div>
                  </td>
                  <td>
                    <div className="flex justify-start px-4 py-3">
                      <p className="text-gray-900">
                        {row?.location?.area}
                      </p>
                    </div>
                  </td>
                  <td>
                    <div className="flex justify-start px-4 py-3">
                      <p className="text-gray-900">
                        {row?.location?.city}, {row?.location?.state}
                      </p>
                    </div>
                  </td>
                  <td>
                    <div className="flex justify-start px-4 py-3">
                      <p className="text-gray-500">
                        {dateFormatter(row?.createdAt)}
                      </p>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <style>
            {`
              .project-table-scroll::-webkit-scrollbar {
                height: 6px;
                width: 6px;
              }

              .project-table-scroll::-webkit-scrollbar-thumb {
                background-color: #949494;
              }
            `}
          </style>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <h6>
            {searchTerm.trim() || statusFilter !== "all"
              ? "No matching projects found"
              : "No Projects found"}
          </h6>
        </div>
      )}

      <ProjectFormDrawer
        open={openProjectDrawer}
        onClose={handleCloseProjectDrawer}
        isEdit
        data={projectOverView}
      />
    </div>
  );
};

export default ProjectsTableSection;
